import React from "react";
import {Alert, Box, Checkbox, FormControlLabel, FormGroup, FormLabel, Stack, Typography} from "@mui/material";
import {CheckCircleOutline as CheckIcon, ErrorOutline as ErrorIcon} from "@mui/icons-material";
import {EVENTOS_ASSINAVEIS, completude, itensDaIntegracao} from "../sistemaPagina";
import CampoAutoSalvo from "./CampoAutoSalvo";
import SecaoCard from "./SecaoCard";
import {CompletudeChip} from "./SistemaVisual";

// As mesmas regras da API (BBF-150), para avisar antes de gravar. Quem decide é a API.
const validarUrl = (valor) => (!valor || /^https:\/\/\S+$/.test(valor) ? '' : 'A URL começa com https://.');
const validarCanal = (valor) => (!valor || /^https:\/\/\S+$/.test(valor) || /^arn:aws:sqs:\S+$/.test(valor) ? '' : 'Use um endpoint https:// ou o ARN de uma fila SQS.');
const validarSegredo = (valor) => {
    if (!valor) return '';
    if (/\s/.test(valor) || valor.length > 200 || !valor.includes('/')) {
        return 'É só o nome do segredo, sem espaços e com pelo menos uma /, como busifarm/dev/sistema/integracao-backoffice. Não cole o valor aqui.';
    }
    return '';
};

const formatarData = (valor) => {
    const data = valor ? new Date(valor) : null;
    return data && !Number.isNaN(data.getTime()) ? data.toLocaleString('pt-BR') : null;
};

export default function IntegracaoSecao({sistema, pendencias, onSalvar, onErro}) {
    const integracao = sistema.integracao || {};
    const eventos = integracao.eventos_assinados || [];
    const itens = itensDaIntegracao(integracao, sistema.metricas || []);
    const prontos = itens.filter((item) => item.ok).length;
    const interno = sistema.status === 'INTERNO';
    const salvar = (campo, rotulo) => (valor) => onSalvar({[campo]: valor || null}, rotulo);

    const alternarEvento = (evento, marcado) => {
        const lista = EVENTOS_ASSINAVEIS.filter((item) => (item === evento ? marcado : eventos.includes(item)));
        onSalvar({eventos_assinados: lista}, 'eventos assinados').catch(onErro);
    };

    return (
        <SecaoCard
            id="integracao"
            titulo="Integração"
            subtitulo={interno
                ? 'Uso interno: o sistema não recebe eventos de assinatura.'
                : `${prontos} de ${itens.length} itens prontos. Integração incompleta é aviso: não impede vender.`}
            chip={<CompletudeChip completude={completude('integracao', {sistema, pendencias})} />}
        >
            <Box sx={{display: 'grid', gap: 3, gridTemplateColumns: {xs: '1fr', md: '3fr 2fr'}}}>
                <Stack spacing={2.5}>
                    <CampoAutoSalvo
                        label="URL de provisionamento"
                        value={integracao.url_provisionamento}
                        placeholder="https://"
                        validar={validarUrl}
                        onSalvar={salvar('url_provisionamento', 'URL de provisionamento')}
                    />
                    <Box component="fieldset" sx={{border: 0, p: 0, m: 0}}>
                        <FormLabel component="legend" sx={{typography: 'caption'}}>Eventos assinados</FormLabel>
                        <FormGroup>
                            {EVENTOS_ASSINAVEIS.map((evento) => (
                                <FormControlLabel
                                    key={evento}
                                    control={<Checkbox size="small" checked={eventos.includes(evento)} onChange={(event) => alternarEvento(evento, event.target.checked)} />}
                                    label={<Typography variant="body2" sx={{fontFamily: 'monospace'}}>{evento}</Typography>}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                    <CampoAutoSalvo
                        label="Envio de uso"
                        value={integracao.canal_envio_uso}
                        placeholder="Endpoint https:// ou ARN da fila SQS"
                        validar={validarCanal}
                        helperText="Eventos e fotos diárias das métricas chegam por aqui."
                        onSalvar={salvar('canal_envio_uso', 'envio de uso')}
                    />
                    <CampoAutoSalvo
                        label="Referência do segredo no Secrets Manager"
                        value={integracao.segredo_ref}
                        mono
                        placeholder="busifarm/<ambiente>/<sistema>/integracao-backoffice"
                        validar={validarSegredo}
                        helperText="Só o nome do segredo. O valor nunca passa por esta tela."
                        inputProps={{autoComplete: 'off', spellCheck: false}}
                        onSalvar={salvar('segredo_ref', 'referência do segredo')}
                    />
                </Stack>
                <Stack spacing={1.5}>
                    <Typography variant="caption" color="text.secondary">Situação</Typography>
                    {interno ? <Alert severity="info" variant="outlined">Sistema de uso interno: a integração não entra nas pendências.</Alert> : null}
                    <Stack component="ul" spacing={1.25} sx={{listStyle: 'none', p: 0, m: 0}}>
                        {itens.map((item) => (
                            <Stack component="li" key={item.id} direction="row" spacing={1} alignItems="flex-start">
                                {item.ok ? <CheckIcon fontSize="small" color="success" /> : <ErrorIcon fontSize="small" color="warning" />}
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>{item.titulo}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.texto}</Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Último contato</Typography>
                        <Typography variant="body2">{formatarData(integracao.ultimo_contato_em) || 'Nunca'}</Typography>
                    </Box>
                </Stack>
            </Box>
        </SecaoCard>
    );
}
